import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageState } from "@/components/common/PageState";
import { Button } from "@/components/ui/button";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useDebounce } from "@/hooks/useDebounce";
import { useHotel } from "@/hooks/useHotel";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { analyticsServicesService } from "@/services/analytics-services-service";
import { useServiceCategories } from "@/components/pages/services/hooks/categories.hooks";
import { usePartnerServiceFilterOptions } from "@/hooks/useHotelServices";
import { cn } from "@/lib/utils";
import { copyAnalyticsDateParams } from "./config/analyticsQueryParams";
import { isRapidPlan } from "./config/planAccess";
import {
  AnalyticsPageProvider,
  useAnalyticsPage,
} from "./context/AnalyticsPageContext";
import { ServicesAllListTable } from "./components/services/ServicesAllListTable";
import { ServicesAllListToolbar } from "./components/services/ServicesAllListToolbar";
import {
  TOP_SERVICES_LIST_PAGE_SIZE,
  type TopServiceRow,
} from "./components/services/mockData";
import {
  DEFAULT_TOP_SERVICES_FILTERS,
  type TopServicesFiltersState,
} from "./components/services/topServicesFilters";
import {
  buildServicesListExportQuery,
  resolveListSegmentCategory,
  toHotelCategoryOptions,
  toPartnerCategoryOptions,
} from "./components/services/topServicesListQuery";
import {
  buildAnalyticsServiceDetailPath,
  type AnalyticsServiceDetailLocationState,
} from "./components/services/serviceDetailNavigation";
import { useAnalyticsServicesList } from "./hooks/useAnalyticsServicesList";
import { downloadBlob } from "@/utils/downloadFile";

function buildAnalyticsServicesBackSearch(
  segment: string | null,
  sourceParams: URLSearchParams,
): string {
  const params = new URLSearchParams();
  params.set("tab", "services");
  copyAnalyticsDateParams(sourceParams, params);

  if (segment) {
    params.set("segment", segment);
  }

  return params.toString();
}

function AnalyticsServicesListPageContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dateRange } = useAnalyticsPage();
  const { userProfile } = useAuthenticatedUser();
  const selectedHotelId = useSelectedHotelId();
  const { hotelId: userHotelId } = useHotel();
  const hasRapidPlan = isRapidPlan(userProfile);

  const hotelId = selectedHotelId ?? userHotelId;
  const segmentParam = searchParams.get("segment");
  const filtersScopeKey = `${hotelId ?? ""}|${resolveListSegmentCategory(
    segmentParam,
    hasRapidPlan,
  )}`;
  // Filters are keyed by hotel and segment: a new scope reads the defaults
  // in the same render, so no request goes out with ids of the old scope.
  const [filtersState, setFiltersState] = useState<{
    key: string;
    filters: TopServicesFiltersState;
  }>({ key: filtersScopeKey, filters: DEFAULT_TOP_SERVICES_FILTERS });
  const appliedFilters =
    filtersState.key === filtersScopeKey
      ? filtersState.filters
      : DEFAULT_TOP_SERVICES_FILTERS;
  const setAppliedFilters = useCallback(
    (filters: TopServicesFiltersState) =>
      setFiltersState({ key: filtersScopeKey, filters }),
    [filtersScopeKey],
  );
  const [searchInput, setSearchInput] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 300);

  const currentPage = Math.max(
    1,
    Number.parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );

  const listSegment = resolveListSegmentCategory(segmentParam, hasRapidPlan);
  const isPartnerSegment = listSegment === "guesteriaPartners";

  // Guesteria Partners segment lists Viator categories of the hotel's partner
  // services; other segments keep the hotel service categories.
  const { data: categoriesResponse, isLoading: isHotelCategoriesLoading } =
    useServiceCategories(
      { hotelId, limit: 200 },
      { enabled: !isPartnerSegment },
    );
  const { data: partnerOptions, isLoading: isPartnerCategoriesLoading } =
    usePartnerServiceFilterOptions(hotelId ?? "", isPartnerSegment);
  const isCategoriesLoading = isPartnerSegment
    ? isPartnerCategoriesLoading
    : isHotelCategoriesLoading;

  const categoryOptions = useMemo(
    () =>
      isPartnerSegment
        ? toPartnerCategoryOptions(partnerOptions?.categories)
        : toHotelCategoryOptions(categoriesResponse?.items),
    [isPartnerSegment, partnerOptions?.categories, categoriesResponse?.items],
  );

  const servicesList = useAnalyticsServicesList({
    dateRange,
    segmentParam,
    hasRapidPlan,
    filters: appliedFilters,
    nameSearch: debouncedSearch,
    page: currentPage,
    pageSize: TOP_SERVICES_LIST_PAGE_SIZE,
    categoryOptions,
  });

  const safePage = Math.min(currentPage, servicesList.totalPages);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);

        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === "") {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });

        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (currentPage !== safePage) {
      updateSearchParams({ page: String(safePage) });
    }
  }, [currentPage, safePage, updateSearchParams]);

  const handleFiltersApply = useCallback(
    (next: TopServicesFiltersState) => {
      setAppliedFilters(next);
      updateSearchParams({ page: "1" });
    },
    [setAppliedFilters, updateSearchParams],
  );

  const handleSearchQueryChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      updateSearchParams({ page: "1" });
    },
    [updateSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      updateSearchParams({ page: String(page) });
    },
    [updateSearchParams],
  );

  const handleServiceClick = useCallback(
    (row: TopServiceRow) => {
      // A Guesteria Partners booking made outside the catalog has no
      // service to navigate to.
      if (row.id == null) {
        return;
      }
      const state: AnalyticsServiceDetailLocationState = { service: row };
      navigate(buildAnalyticsServiceDetailPath(row.id, searchParams), {
        state,
      });
    },
    [navigate, searchParams],
  );

  const handleBack = useCallback(() => {
    navigate({
      pathname: "/analytics",
      search: buildAnalyticsServicesBackSearch(segmentParam, searchParams),
    });
  }, [navigate, searchParams, segmentParam]);

  const handleExport = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!servicesList.isReady || servicesList.total === 0) {
        return;
      }

      setIsExporting(true);

      try {
        const blob = await analyticsServicesService.exportList(
          buildServicesListExportQuery(servicesList.listQuery, format),
        );
        downloadBlob(
          blob,
          `analytics-services-${servicesList.hotelId}-${servicesList.from}-${servicesList.to}.${format}`,
        );
        toast.success(
          t(
            "analytics.services.list.exportSuccess",
            "Services export downloaded",
          ),
        );
      } catch {
        toast.error(
          t("analytics.services.list.exportError", "Failed to export services"),
        );
      } finally {
        setIsExporting(false);
      }
    },
    [servicesList, t],
  );

  const isInitialLoading =
    !servicesList.isReady || servicesList.isLoading || isCategoriesLoading;
  const isContentPending = servicesList.isFetching && !isInitialLoading;

  if (servicesList.isError) {
    return (
      <div className="p-6 font-sans">
        <PageState
          text={t(
            "analytics.services.list.loadError",
            "Failed to load services. Please try again.",
          )}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 font-sans">
      <Button
        type="button"
        variant="ghost"
        onClick={handleBack}
        className="h-8 w-fit justify-start self-start rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
      >
        <Undo2 className="h-6 w-6" />
        {t("analytics.common.back", "Back")}
      </Button>

      <ServicesAllListToolbar
        appliedFilters={appliedFilters}
        onFiltersApply={handleFiltersApply}
        categoryOptions={categoryOptions}
        searchQuery={searchInput}
        onSearchQueryChange={handleSearchQueryChange}
        onExport={handleExport}
        isExportDisabled={!servicesList.isReady || servicesList.total === 0}
        isExporting={isExporting}
      />

      {isInitialLoading ? (
        <PageState
          text={t("analytics.services.list.loading", "Loading services...")}
        />
      ) : (
        <div
          className={cn(
            "transition-opacity duration-150",
            isContentPending && "opacity-70",
          )}
        >
          <ServicesAllListTable
            rows={servicesList.rows}
            segment={listSegment}
            currentPage={safePage}
            totalPages={servicesList.totalPages}
            onPageChange={handlePageChange}
            onRowClick={handleServiceClick}
          />
        </div>
      )}
    </div>
  );
}

export const AnalyticsServicesListPage: React.FC = () => {
  return (
    <AnalyticsPageProvider>
      <AnalyticsServicesListPageContent />
    </AnalyticsPageProvider>
  );
};
